import Phaser from 'phaser';
import type { InboxGame } from '../../api/inbox-types.js';
import { mapsById } from '../../maps/index.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { formatInboxTurnExpiry } from './turnTimerFormat.js';

// Shared card renderer used by InboxScene (full list) and HomeScene
// (inline preview). Lives here so the visual treatment — YOUR TURN
// gold accent, badge pulse, host/you roster tags, WON/LOST treatment
// for finished games — stays in one place.
//
// Pure draw function: returns the rendered height so callers can
// advance their layout cursor. All visual children are appended to
// the supplied parent container (so masks/scroll containers from the
// host scene compose cleanly).

const CARD_RADIUS = 8;
const CARD_PAD_X = 22;
const CARD_PAD_Y = 18;
const CARD_HEIGHT = 168;

const BADGE_YOUR_TURN_HEX = '#ffd54f';
const BADGE_OPPONENT_HEX = COLORS.textSecondary;
const BADGE_LOBBY_HEX = COLORS.accentAmberHex;
const BADGE_FINISHED_HEX = '#5c6b82';
const BADGE_WON_HEX = COLORS.accentCyanHex;
const BADGE_LOST_HEX = '#5c6b82';
/** Lavender purple — distinct from gold (YOUR TURN) and amber
 *  (WAITING). Pulses to signal "social action wanted." */
const BADGE_INVITED_HEX = '#a78bfa';

// Text-scale to match the inbox / settings / about pages.
const TEXT_SCALE = 1.5;
function fs(base: number): string {
  return `${Math.round(base * TEXT_SCALE)}px`;
}

export interface InboxGameCardOpts {
  readonly scene: Phaser.Scene;
  /** Container to append all card children into. Typically a scene
   *  content container that the parent scene manages (scroll mask,
   *  etc.). */
  readonly parent: Phaser.GameObjects.Container;
  /** Top-left x of the card in the parent's local coords. */
  readonly x: number;
  /** Top-left y of the card in the parent's local coords. */
  readonly y: number;
  readonly width: number;
  readonly game: InboxGame;
  /** Signed-in user's auth.users.id. Used to mark the "(you)" tag
   *  in the roster + drive the YOUR TURN styling — pass the actual
   *  user id, not a synthetic player id. */
  readonly meUserId: string;
  /** Fired when the card is tapped. Typically navigates to
   *  /game/:id via scene.start('Join', { gameId }). For INVITED
   *  cards this is the Accept handler — the whole-card tap zone is
   *  suppressed and only the Accept button fires onTap. */
  readonly onTap: (game: InboxGame) => void;
  /** Fired when the INVITED card's Decline button is tapped.
   *  Required when game.invitedToGame is true; ignored otherwise.
   *  Receives the game so the caller can extract invitedSeatIndex. */
  readonly onDecline?: (game: InboxGame) => void;
}

export interface InboxGameCardResult {
  /** Total rendered height of the card. Caller advances its layout
   *  cursor by this + their preferred gap. */
  readonly height: number;
}

export function drawInboxGameCard(
  opts: InboxGameCardOpts
): InboxGameCardResult {
  const { scene, parent, x, y, width, game, meUserId, onTap, onDecline } = opts;
  const isInvited = game.invitedToGame;
  const cardH = CARD_HEIGHT;
  const isYourTurn = game.status === 'active' && game.isYourTurn;
  // Border color priority: INVITED purple > YOUR TURN gold > muted.
  const accentColorInt = isInvited
    ? hexToInt(BADGE_INVITED_HEX)
    : isYourTurn
      ? hexToInt(BADGE_YOUR_TURN_HEX)
      : COLORS.borderSubtleInt;

  // Card background. YOUR TURN cards get a warmer fill tint so they
  // feel lit; INVITED cards get a faint purple tint for the same
  // "this needs your attention" pop. Defaults to the standard deep
  // navy fill.
  const bg = scene.add.graphics();
  const fillColor = isInvited
    ? 0x18152b
    : isYourTurn
      ? 0x1a1e15
      : 0x0e1a2b;
  const borderWidth = isInvited || isYourTurn ? 2 : 1;
  bg.fillStyle(fillColor, 1);
  bg.fillRoundedRect(x, y, width, cardH, CARD_RADIUS);
  bg.lineStyle(borderWidth, accentColorInt, 1);
  bg.strokeRoundedRect(x, y, width, cardH, CARD_RADIUS);
  parent.add(bg);

  // Left accent bar — full card height. Purple for INVITED, gold
  // for YOUR TURN, none for the rest.
  if (isInvited) {
    const accent = scene.add.graphics();
    accent.fillStyle(hexToInt(BADGE_INVITED_HEX), 1);
    accent.fillRect(x, y, 6, cardH);
    parent.add(accent);
  } else if (isYourTurn) {
    const accent = scene.add.graphics();
    accent.fillStyle(hexToInt(BADGE_YOUR_TURN_HEX), 1);
    accent.fillRect(x, y, 6, cardH);
    parent.add(accent);
  }

  // Top row: status badge (left) + last-action time OR action
  // buttons (right). INVITED cards swap the timestamp for compact
  // ACCEPT/DECLINE buttons in the same slot — the action is the
  // most useful info on that row for a pending invite.
  const badgeY = y + CARD_PAD_Y;
  drawStatusBadge(scene, parent, x + CARD_PAD_X, badgeY, game);
  if (!isInvited) {
    drawTimeText(
      scene,
      parent,
      x + width - CARD_PAD_X,
      badgeY,
      game.lastActionAt
    );
  }

  // Map name — large header, dominant element of the row.
  const mapName = (mapsById[game.mapId]?.name ?? game.mapId).toUpperCase();
  const mapY = y + 56;
  const mapText = scene.add
    .text(x + CARD_PAD_X, mapY, mapName, {
      fontFamily: FONT_HEADER,
      fontSize: fs(22),
      color: isYourTurn ? BADGE_YOUR_TURN_HEX : COLORS.accentCyanHex,
      fontStyle: '700',
    })
    .setLetterSpacing(3)
    .setOrigin(0, 0);
  parent.add(mapText);

  // INVITED card: timestamp right-aligned on the map row. Empty
  // space to the right of the (short) map header is the natural
  // home for it without disturbing the standard card layout.
  if (isInvited) {
    const invitedTimeText = scene.add
      .text(
        x + width - CARD_PAD_X,
        mapY + 12, // vertically centered against the larger map text
        relativeTime(game.lastActionAt),
        {
          fontFamily: FONT_MONO,
          fontSize: fs(11),
          color: COLORS.textMuted,
        }
      )
      .setLetterSpacing(1)
      .setOrigin(1, 0);
    parent.add(invitedTimeText);
  }

  // Subtitle — small line under the map name.
  // YOUR-TURN cards swap the game-type info for the "Turn expires
  // in Xh" countdown — the timer is the actionable info on those
  // cards. Other states (waiting, opponent's turn, finished,
  // invited) stay with the game-type subtitle. v0.9.9 Hourglass.
  const subtitleY = y + 92;
  const showTimerSubtitle =
    isYourTurn && game.turnExpiresAt != null;
  const subtitleText = showTimerSubtitle
    ? formatInboxTurnExpiry(
        new Date(game.turnExpiresAt as string).getTime() - Date.now()
      )
    : formatGameType(game);
  const subtitleColor = showTimerSubtitle
    ? BADGE_YOUR_TURN_HEX
    : COLORS.textMuted;
  const subtitle = scene.add
    .text(x + CARD_PAD_X, subtitleY, subtitleText, {
      fontFamily: FONT_MONO,
      fontSize: fs(11),
      color: subtitleColor,
    })
    .setLetterSpacing(2)
    .setOrigin(0, 0);
  parent.add(subtitle);

  // Bottom row: player roster. Same position regardless of state
  // now that INVITED cards fit in standard CARD_HEIGHT (the buttons
  // moved up into the badge row's right slot).
  const rosterY = y + cardH - CARD_PAD_Y - 14;
  drawPlayerRoster(scene, parent, x + CARD_PAD_X, rosterY, game, meUserId);

  if (isInvited) {
    // Compact ACCEPT + DECLINE buttons in the top-right slot, with
    // the timestamp directly underneath them. Buttons stay badge-
    // aligned (center at badgeY + 13) so they sit visually beside
    // the INVITED badge. Map name gets shifted down on INVITED
    // cards only to give the timestamp clean breathing room
    // between the buttons and the map header.
    //
    // Inlined (graphics + text + interactive zone) rather than the
    // shared Button class because Button adds to scene root, which
    // would float outside the inbox scroll mask.
    const btnH = 28;
    const acceptW = 80;
    const declineW = 80;
    const btnGap = 8;
    const btnRowY = badgeY + 13;
    const declineX = x + width - CARD_PAD_X - declineW / 2;
    const acceptX = declineX - declineW / 2 - btnGap - acceptW / 2;

    drawInlineButton({
      scene,
      parent,
      x: acceptX,
      y: btnRowY,
      width: acceptW,
      height: btnH,
      label: 'ACCEPT',
      fill: COLORS.buttonBgPrimaryInt,
      stroke: COLORS.buttonStrokePrimaryInt,
      textColor: COLORS.textPrimary,
      onTap: () => onTap(game),
    });

    drawInlineButton({
      scene,
      parent,
      x: declineX,
      y: btnRowY,
      width: declineW,
      height: btnH,
      label: 'DECLINE',
      fill: COLORS.buttonBgInt,
      stroke: COLORS.buttonStrokeInt,
      textColor: COLORS.textSecondary,
      onTap: () => {
        if (onDecline) onDecline(game);
      },
    });
    // Timestamp for INVITED cards lives on the map row (handled
    // up above) — no second timestamp here.
  } else {
    // Whole card tappable for non-invited variants.
    const hit = scene.add
      .zone(x, y, width, cardH)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => onTap(game));
    parent.add(hit);
  }

  return { height: cardH };
}

/** Compact inline button — graphics + text + interactive zone, all
 *  appended to the supplied parent container so they transform with
 *  any scene-level scroll/mask. Used for INVITED card buttons where
 *  the shared Button class can't sit inside the inbox scroll mask. */
function drawInlineButton(opts: {
  readonly scene: Phaser.Scene;
  readonly parent: Phaser.GameObjects.Container;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly label: string;
  readonly fill: number;
  readonly stroke: number;
  readonly textColor: string;
  readonly onTap: () => void;
}): void {
  const {
    scene,
    parent,
    x,
    y,
    width,
    height,
    label,
    fill,
    stroke,
    textColor,
    onTap,
  } = opts;

  const bg = scene.add.rectangle(x, y, width, height, fill);
  bg.setStrokeStyle(2, stroke);
  bg.setInteractive({ useHandCursor: true });
  bg.on('pointerup', () => onTap());
  parent.add(bg);

  const text = scene.add
    .text(x, y, label, {
      fontFamily: FONT_HEADER,
      fontSize: '12px',
      color: textColor,
      fontStyle: '700',
    })
    .setLetterSpacing(2)
    .setOrigin(0.5);
  parent.add(text);
}

function drawStatusBadge(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  x: number,
  y: number,
  g: InboxGame
): void {
  const { label, color, pulse } = badgeFor(g);
  const colorInt = hexToInt(color);

  const padX = 12;
  const padY = 6;

  const text = scene.add
    .text(0, 0, label, {
      fontFamily: FONT_MONO,
      fontSize: fs(11),
      color: '#040713',
      fontStyle: '900',
    })
    .setLetterSpacing(2)
    .setOrigin(0, 0);
  const w = text.width + padX * 2;
  const h = text.height + padY * 2;

  // Wrap bg + text in a Container so the pulse tween scales around
  // the badge center (containers transform around their own origin)
  // rather than the graphics object's (0, 0).
  const wrapper = scene.add.container(x + w / 2, y + h / 2);
  const bg = scene.add.graphics();
  bg.fillStyle(colorInt, 1);
  bg.fillRoundedRect(-w / 2, -h / 2, w, h, h / 2);
  wrapper.add(bg);
  text.setPosition(-w / 2 + padX, -h / 2 + padY);
  wrapper.add(text);
  parent.add(wrapper);

  if (pulse) {
    // Coordinated alpha + scale tweens for the YOUR TURN badge —
    // faster cycle + bigger swing than the original so it actually
    // commands attention against the page chrome.
    scene.tweens.add({
      targets: wrapper,
      alpha: { from: 1, to: 0.55 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    scene.tweens.add({
      targets: wrapper,
      scale: { from: 1, to: 1.1 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
}

function drawTimeText(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  rightX: number,
  y: number,
  iso: string
): void {
  const t = scene.add
    .text(rightX, y + 3, relativeTime(iso), {
      fontFamily: FONT_MONO,
      fontSize: fs(11),
      color: COLORS.textMuted,
    })
    .setLetterSpacing(1)
    .setOrigin(1, 0);
  parent.add(t);
}

function drawPlayerRoster(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  x: number,
  y: number,
  g: InboxGame,
  meUserId: string
): void {
  let cx = x;
  const swatchSize = 12;
  const swatchGap = 6;
  const sepGap = 8;
  const sortedSeats = [...g.seats].sort((a, b) => a.seatIndex - b.seatIndex);
  // For finished games, tint by team result: winning team's names
  // pick up the cyan accent (matching the WON badge), losing team's
  // names stay muted. For active/lobby, fall back to the existing
  // "isMe" tint so the user's own seat is the only highlighted one.
  const finished = g.status === 'finished' && g.winningTeam != null;
  sortedSeats.forEach((seat, idx) => {
    const swatch = scene.add.graphics();
    swatch.fillStyle(hexToInt(seat.color), 1);
    swatch.fillRoundedRect(cx, y, swatchSize, swatchSize, 2);
    parent.add(swatch);
    cx += swatchSize + swatchGap;

    const isMe = seat.userId === meUserId;
    const isHost = seat.seatIndex === 0;
    const tag =
      isMe && isHost
        ? ' (you, host)'
        : isMe
          ? ' (you)'
          : isHost
            ? ' (host)'
            : '';
    const label = `${seat.username}${tag}`;
    const nameColor = finished
      ? seat.team === g.winningTeam
        ? COLORS.accentCyanHex
        : COLORS.textMuted
      : isMe
        ? COLORS.accentCyanHex
        : COLORS.textSecondary;
    const txt = scene.add
      .text(cx, y - 4, label, {
        fontFamily: FONT_MONO,
        fontSize: fs(12),
        color: nameColor,
      })
      .setOrigin(0, 0);
    parent.add(txt);
    cx += txt.width;

    if (idx < sortedSeats.length - 1) {
      const sep = scene.add
        .text(cx + 4, y - 4, '·', {
          fontFamily: FONT_MONO,
          fontSize: fs(12),
          color: COLORS.textMuted,
        })
        .setOrigin(0, 0);
      parent.add(sep);
      cx += sep.width + sepGap;
    }
  });
}

// --- Pure helpers ---

/** Sort key for inbox lists. Lower = surfaces first. INVITED ranks
 *  above YOUR TURN because pending-decision items demand explicit
 *  acceptance (you can have 5 YOUR TURN games but each only needs a
 *  tap; an invite needs Accept/Decline). */
export function inboxGameBucketRank(g: InboxGame): number {
  if (g.invitedToGame) return -1;
  if (g.status === 'active' && g.isYourTurn) return 0;
  if (g.status === 'active') return 1;
  if (g.status === 'lobby') return 2;
  return 3;
}

function badgeFor(g: InboxGame): {
  label: string;
  color: string;
  pulse: boolean;
} {
  if (g.invitedToGame) {
    return { label: 'INVITED', color: BADGE_INVITED_HEX, pulse: true };
  }
  if (g.status === 'finished') {
    // localOutcome tells us whether the signed-in user's team won.
    // Null fallback (server didn't compute, or user wasn't seated)
    // keeps the legacy FINISHED label so the row still renders.
    if (g.localOutcome === 'won') {
      return { label: 'WON', color: BADGE_WON_HEX, pulse: false };
    }
    if (g.localOutcome === 'lost') {
      return { label: 'LOST', color: BADGE_LOST_HEX, pulse: false };
    }
    return { label: 'FINISHED', color: BADGE_FINISHED_HEX, pulse: false };
  }
  if (g.status === 'lobby') {
    const seatedHumans = g.seats.filter((s) => s.kind === 'human').length;
    // Post-v0.9.5: target_seats drives auto-start. The "READY TO
    // START" intermediate state is gone — once the count hits
    // target_seats the server auto-flips status to 'active' so a
    // lobby row with seatedHumans == targetSeats only exists for
    // the few seconds before realtime delivers the transition.
    return {
      label: `WAITING · ${seatedHumans}/${g.targetSeats}`,
      color: BADGE_LOBBY_HEX,
      pulse: false,
    };
  }
  if (g.isYourTurn) {
    return { label: 'YOUR TURN', color: BADGE_YOUR_TURN_HEX, pulse: true };
  }
  return { label: 'OPPONENT', color: BADGE_OPPONENT_HEX, pulse: false };
}

/**
 * Game-type subtitle. FFA shows the planned player count from
 * targetSeats (matches what's filled for finished games, and what's
 * planned for lobby/active). 2v2-ai is invariant by definition.
 */
function formatGameType(g: InboxGame): string {
  if (g.mode === '2v2-ai') return '2v2 VS AI';
  return `FFA · ${g.targetSeats} PLAYERS`;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function hexToInt(hex: string): number {
  let s = hex.startsWith('#') ? hex.slice(1) : hex;
  if (s.length === 3) {
    s = s
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return parseInt(s, 16);
}
